// 📄 utils/xmlStructureUtils.ts
// Motor de comparación del "Validador de Estructura": a diferencia de
// compararXML.tsx (que extrae un puñado de campos de negocio conocidos vía
// XPath fijo), acá el objetivo es recorrer CUALQUIER XML sin saber de
// antemano qué tags trae, para comparar forma/estructura contra una
// plantilla de referencia — no valores de negocio entre sí.

export interface LongitudExcedida {
  path: string;
  largoPlantilla: number;
  largoDocumento: number;
  valorPlantilla: string;
  valorDocumento: string;
}

// Campo con su valor real (faltante = el que tenía la plantilla; nuevo =
// el que trae el documento) — antes solo se guardaba el path, sin valor.
export interface CampoConValor {
  path: string;
  valor: string;
}

export interface ResultadoComparacionEstructura {
  longitudExcedida: LongitudExcedida[];
  camposFaltantes: CampoConValor[];
  camposNuevos: CampoConValor[];
}

export const parseXmlString = (xml: string): Document => {
  // BOM (U+FEFF) al inicio del archivo — típico en XML exportado de SAP —
  // rompe el parser con "Start tag expected, '<' not found" aunque el XML
  // sea válido. Se limpia acá porque es el único punto de entrada de
  // parseo, así cualquier contenido ya guardado con BOM también se
  // recupera sin necesidad de volver a subirlo.
  const BOM = String.fromCharCode(0xfeff);
  const limpio = xml.startsWith(BOM) ? xml.slice(1) : xml;
  const parser = new DOMParser();
  const doc = parser.parseFromString(limpio, "text/xml");
  const parserError = doc.getElementsByTagName("parsererror")[0];
  if (parserError) {
    throw new Error("El XML no es válido: " + (parserError.textContent || "").trim());
  }
  return doc;
};

const nombreTag = (el: Element): string => el.localName || el.tagName;

/**
 * Recorre el árbol y arma un mapa path→valor. Un elemento sin hijos-elemento
 * es una "hoja" (su textContent es el valor, aunque esté vacío — el tag
 * sigue contando como presente). Los tags hermanos repetidos (p. ej. varios
 * <Item>) se distinguen con un índice: Item[0], Item[1]... Los namespaces
 * se ignoran (localName), igual criterio que ya usa compararXML.tsx con
 * local-name() en sus XPath.
 */
const recorrer = (nodo: Element, pathActual: string, mapa: Record<string, string>): void => {
  const hijos = Array.from(nodo.children);

  if (hijos.length === 0) {
    mapa[pathActual] = (nodo.textContent || "").trim();
    return;
  }

  const totalPorTag: Record<string, number> = {};
  hijos.forEach((hijo) => {
    const tag = nombreTag(hijo);
    totalPorTag[tag] = (totalPorTag[tag] || 0) + 1;
  });

  const vistosPorTag: Record<string, number> = {};
  hijos.forEach((hijo) => {
    const tag = nombreTag(hijo);
    const indice = vistosPorTag[tag] || 0;
    vistosPorTag[tag] = indice + 1;

    const segmento = totalPorTag[tag] > 1 ? `${tag}[${indice}]` : tag;
    const path = pathActual ? `${pathActual}/${segmento}` : segmento;
    recorrer(hijo, path, mapa);
  });
};

export const xmlToPathMap = (xmlDoc: Document): Record<string, string> => {
  const mapa: Record<string, string> = {};
  const raiz = xmlDoc.documentElement;
  if (!raiz) return mapa;
  recorrer(raiz, nombreTag(raiz), mapa);
  return mapa;
};

export const compararEstructuras = (
  plantillaMap: Record<string, string>,
  documentoMap: Record<string, string>
): ResultadoComparacionEstructura => {
  const longitudExcedida: LongitudExcedida[] = [];
  const camposFaltantes: CampoConValor[] = [];

  for (const path of Object.keys(plantillaMap)) {
    if (!(path in documentoMap)) {
      camposFaltantes.push({ path, valor: plantillaMap[path] });
      continue;
    }
    const valorPlantilla = plantillaMap[path];
    const valorDocumento = documentoMap[path];
    const largoPlantilla = valorPlantilla.length;
    const largoDocumento = valorDocumento.length;
    if (largoDocumento > largoPlantilla) {
      longitudExcedida.push({ path, largoPlantilla, largoDocumento, valorPlantilla, valorDocumento });
    }
  }

  const camposNuevos = Object.keys(documentoMap)
    .filter((path) => !(path in plantillaMap))
    .map((path) => ({ path, valor: documentoMap[path] }));

  return { longitudExcedida, camposFaltantes, camposNuevos };
};

// ── Presentación: índices base-1, truncado y agrupado por ítem ────────────
// Todo lo de acá abajo es SOLO para mostrarle al usuario — nunca se usa
// para las claves de comparación de arriba (esas siguen 0-based, es lo que
// realmente distingue cada nodo del XML).

/**
 * El validador itera arrays desde 0 (Item[0], Item[1]...) pero en el XML
 * real los elementos se cuentan desde 1 — esta es la ÚNICA conversión de
 * esa diferencia, y es puramente visual: recibe un path ya armado por
 * xmlToPathMap y le suma 1 a cada índice entre corchetes.
 */
export const formatearPathVisual = (path: string): string =>
  path.replace(/\[(\d+)\]/g, (_match, indice) => `[${Number(indice) + 1}]`);

/**
 * Prefijo de segmentos (separados por "/") común a TODOS los paths recibidos
 * — usado para mostrar el contexto raíz una sola vez en el header del panel
 * en vez de repetirlo en cada fila. Se detiene siempre un segmento antes del
 * más corto de los paths, así ninguna fila queda con el path vacío (si solo
 * hay una discrepancia, no tiene sentido "prefijo = el path completo").
 */
export const detectarPrefijoComun = (paths: string[]): string => {
  if (paths.length === 0) return "";
  const listasSegmentos = paths.map((p) => p.split("/"));
  const largoMinimo = Math.min(...listasSegmentos.map((s) => s.length));
  const comunes: string[] = [];
  for (let i = 0; i < largoMinimo - 1; i++) {
    const segmento = listasSegmentos[0][i];
    if (listasSegmentos.every((s) => s[i] === segmento)) {
      comunes.push(segmento);
    } else {
      break;
    }
  }
  return comunes.join("/");
};

/**
 * Trunca un valor largo para mostrarlo en una fila sin romper el layout —
 * el valor completo siempre va en el `title` (tooltip) de quien lo use,
 * truncado o no.
 */
export const truncarValor = (valor: string, maxLargo = 60): string =>
  valor.length > maxLargo ? `${valor.slice(0, maxLargo)}…` : valor;

// Quita todos los índices [n] de un path — es la operación inversa/
// complementaria a formatearPathVisual: acá se usa para MATCHEAR un path
// real (que trae índices, ej. "Item[0]/Description") contra un tag sin
// índices (ej. una restricción configurada, que aplica a todas las
// ocurrencias del tag).
const quitarIndices = (path: string): string => path.replace(/\[\d+\]/g, "");

export type CategoriaDiscrepancia = "longitud" | "faltante" | "nuevo";
export type SeveridadDiscrepancia = "error" | "advertencia" | "info";

export interface DiscrepanciaEntry {
  path: string;
  categoria: CategoriaDiscrepancia;
  severidad: SeveridadDiscrepancia;
  badge: string;
  helperText?: string;
  valorPlantilla?: string;
  valorDocumento?: string;
  largoPlantilla?: number;
  largoDocumento?: number;
}

/**
 * Aplana las 3 categorías del resultado en una sola lista de filas, cada
 * una con su severidad/badge ya resueltos:
 * - longitud: si el tag tiene `longitudMaxima` configurada en `restricciones`
 *   Y el valor nuevo la supera → "error" (regla de negocio violada). En
 *   cualquier otro caso (sin restricción configurada, o la cumple y solo
 *   cambió respecto a la plantilla) → "advertencia", es una diferencia sin
 *   regla de negocio que la marque como incorrecta.
 * - faltante: siempre "error" — el campo de la plantilla no está.
 * - nuevo: siempre "info" — no es un problema, solo no estaba en la plantilla.
 */
export const construirEntradas = (
  resultado: ResultadoComparacionEstructura,
  restricciones: RestriccionTag[] = []
): DiscrepanciaEntry[] => {
  const longitudMaximaPorTag = new Map<string, number>();
  restricciones.forEach((r) => {
    if (typeof r.restricciones?.longitudMaxima === "number") {
      longitudMaximaPorTag.set(r.tag, r.restricciones.longitudMaxima);
    }
  });

  return [
    ...resultado.longitudExcedida.map((r): DiscrepanciaEntry => {
      const maximo = longitudMaximaPorTag.get(quitarIndices(r.path));
      const violaRestriccion = typeof maximo === "number" && r.largoDocumento > maximo;
      return {
        path: r.path,
        categoria: "longitud",
        severidad: violaRestriccion ? "error" : "advertencia",
        badge: violaRestriccion ? "Long. excedida" : "Diferencia",
        valorPlantilla: r.valorPlantilla,
        valorDocumento: r.valorDocumento,
        largoPlantilla: r.largoPlantilla,
        largoDocumento: r.largoDocumento
      };
    }),
    ...resultado.camposFaltantes.map((c): DiscrepanciaEntry => ({
      path: c.path,
      categoria: "faltante",
      severidad: "error",
      badge: "Faltante",
      helperText: "Este campo existe en la plantilla pero no se encontró en el documento.",
      valorPlantilla: c.valor
    })),
    ...resultado.camposNuevos.map((c): DiscrepanciaEntry => ({
      path: c.path,
      categoria: "nuevo",
      severidad: "info",
      badge: "Campo nuevo",
      helperText: "Este campo no estaba en la plantilla de referencia.",
      valorDocumento: c.valor
    }))
  ];
};

export interface GrupoDiscrepancias {
  key: string;
  label: string;
  entries: DiscrepanciaEntry[];
}

// Key del grupo de campos que no pertenecen a ningún array — exportada
// para que quien renderice (ver ValidadorEstructura.tsx) pueda distinguirlo
// del resto sin duplicar el string a mano.
export const GRUPO_GENERAL_KEY = "general";

/**
 * Agrupa las filas por el primer array encontrado en su path (ej. todo lo
 * que cuelga de "Item[2]" cae en el grupo "Item 3", ya con el índice
 * visual base-1). Lo que no pertenece a ningún array cae en "General".
 * "General" siempre va primero; el resto, ordenado por índice ascendente.
 */
export const agruparEntradas = (entries: DiscrepanciaEntry[]): GrupoDiscrepancias[] => {
  const grupos = new Map<string, GrupoDiscrepancias>();

  for (const entry of entries) {
    const match = entry.path.match(/([A-Za-z0-9_]+)\[(\d+)\]/);
    const key = match ? `${match[1]}-${match[2]}` : GRUPO_GENERAL_KEY;
    const label = match ? `${match[1]} ${Number(match[2]) + 1}` : "General";

    if (!grupos.has(key)) grupos.set(key, { key, label, entries: [] });
    grupos.get(key)!.entries.push(entry);
  }

  const lista = [...grupos.values()];
  lista.sort((a, b) => {
    if (a.key === GRUPO_GENERAL_KEY) return -1;
    if (b.key === GRUPO_GENERAL_KEY) return 1;
    const indiceA = Number(a.key.split("-").pop());
    const indiceB = Number(b.key.split("-").pop());
    return indiceA - indiceB;
  });

  return lista;
};

// ── Restricciones por tag: reglas de negocio independientes de la
// comparación estructural de arriba (ver comentario en ValidadorEstructura.tsx
// sobre la separación conceptual entre Panel de Discrepancias y Panel de
// Restricciones). ──────────────────────────────────────────────────────────

export interface RestriccionReglas {
  longitudMaxima?: number;
  longitudMinima?: number;
  soloNumerico?: boolean;
  requerido?: boolean;
  valoresPermitidos?: string[];
}

export interface RestriccionTag {
  // Path completo SIN índices — aplica a todas las ocurrencias del tag
  // (ej. "PurchasingContract/Item/Description" cubre Item[0], Item[1]...).
  tag: string;
  restricciones: RestriccionReglas;
  // Nota libre del admin/usuario explicando el porqué de la restricción.
  descripcion?: string;
}

export type TipoRestriccionViolada = "longitudMaxima" | "longitudMinima" | "soloNumerico" | "requerido" | "valoresPermitidos";

export interface ResultadoRestriccion {
  tag: string;
  tagConIndice: string;
  valorActual: string;
  restriccionViolada: TipoRestriccionViolada;
  detalle: string;
  descripcionAdmin?: string;
}

/**
 * Tags únicos presentes en un XML ya parseado, sin índices y sin
 * duplicados — reutiliza xmlToPathMap (mismo recorrido que usa la
 * comparación de estructura) en vez de recorrer el árbol de nuevo. Usado
 * por RestriccionesTagEditor.tsx para armar la lista de tags configurables.
 */
export const extraerTagsUnicos = (xmlDoc: Document): string[] => {
  const mapa = xmlToPathMap(xmlDoc);
  const unicos = new Set(Object.keys(mapa).map(quitarIndices));
  return [...unicos].sort((a, b) => a.localeCompare(b));
};

/**
 * Valida el documento del usuario contra las restricciones técnicas de la
 * plantilla — independiente de si el valor coincide con la plantilla (eso
 * lo cubre compararEstructuras/agruparEntradas arriba). Un tag puede
 * disparar más de una regla a la vez, cada una es una entrada separada.
 *
 * Criterio para valores vacíos (ver discusión con el usuario): un tag
 * ausente del documento solo viola algo si la regla es `requerido`; un tag
 * presente pero con valor vacío tampoco evalúa longitudMinima/soloNumerico/
 * valoresPermitidos (evita ruido en campos opcionales vacíos) — solo
 * `requerido` cubre ese caso. `longitudMaxima` es indiferente a un valor
 * vacío (0 nunca supera un máximo positivo), así que no hace falta excluirla.
 */
export const validarRestricciones = (documentoXML: string, restricciones: RestriccionTag[]): ResultadoRestriccion[] => {
  if (!restricciones || restricciones.length === 0) return [];

  let documentoMap: Record<string, string>;
  try {
    documentoMap = xmlToPathMap(parseXmlString(documentoXML));
  } catch {
    // Un XML inválido ya se reporta aparte (ver handleValidar en
    // ValidadorEstructura.tsx) — acá no se duplica el error.
    return [];
  }

  const resultados: ResultadoRestriccion[] = [];

  for (const regla of restricciones) {
    const r = regla.restricciones || {};
    const ocurrencias = Object.keys(documentoMap).filter((path) => quitarIndices(path) === regla.tag);

    if (ocurrencias.length === 0) {
      if (r.requerido) {
        resultados.push({
          tag: regla.tag,
          tagConIndice: regla.tag,
          valorActual: "",
          restriccionViolada: "requerido",
          detalle: `El campo "${regla.tag}" es requerido pero no está presente en el documento.`,
          descripcionAdmin: regla.descripcion
        });
      }
      continue;
    }

    for (const path of ocurrencias) {
      const valor = documentoMap[path];
      const tagConIndice = formatearPathVisual(path);
      const vacio = valor.trim().length === 0;

      if (r.requerido && vacio) {
        resultados.push({
          tag: regla.tag,
          tagConIndice,
          valorActual: valor,
          restriccionViolada: "requerido",
          detalle: `El campo "${regla.tag}" es requerido pero está vacío.`,
          descripcionAdmin: regla.descripcion
        });
      }

      if (typeof r.longitudMaxima === "number" && valor.length > r.longitudMaxima) {
        resultados.push({
          tag: regla.tag,
          tagConIndice,
          valorActual: valor,
          restriccionViolada: "longitudMaxima",
          detalle: `Longitud ${valor.length} supera el máximo de ${r.longitudMaxima} caracteres.`,
          descripcionAdmin: regla.descripcion
        });
      }

      if (!vacio && typeof r.longitudMinima === "number" && valor.length < r.longitudMinima) {
        resultados.push({
          tag: regla.tag,
          tagConIndice,
          valorActual: valor,
          restriccionViolada: "longitudMinima",
          detalle: `Longitud ${valor.length} es menor al mínimo de ${r.longitudMinima} caracteres.`,
          descripcionAdmin: regla.descripcion
        });
      }

      if (!vacio && r.soloNumerico && !/^\d+$/.test(valor)) {
        resultados.push({
          tag: regla.tag,
          tagConIndice,
          valorActual: valor,
          restriccionViolada: "soloNumerico",
          detalle: `El valor "${valor}" no es numérico.`,
          descripcionAdmin: regla.descripcion
        });
      }

      if (!vacio && r.valoresPermitidos && r.valoresPermitidos.length > 0 && !r.valoresPermitidos.includes(valor)) {
        resultados.push({
          tag: regla.tag,
          tagConIndice,
          valorActual: valor,
          restriccionViolada: "valoresPermitidos",
          detalle: `El valor "${valor}" no está en la lista de valores permitidos.`,
          descripcionAdmin: regla.descripcion
        });
      }
    }
  }

  return resultados;
};

export interface GrupoRestriccion {
  tag: string;
  entries: ResultadoRestriccion[];
}

/**
 * Agrupa los resultados de validarRestricciones por tag (a diferencia de
 * agruparEntradas, que agrupa por índice de array) — orden alfabético por
 * tag, mismo criterio que la lista de configuración en RestriccionesTagEditor.tsx.
 */
export const agruparPorTag = (resultados: ResultadoRestriccion[]): GrupoRestriccion[] => {
  const grupos = new Map<string, ResultadoRestriccion[]>();
  for (const entry of resultados) {
    if (!grupos.has(entry.tag)) grupos.set(entry.tag, []);
    grupos.get(entry.tag)!.push(entry);
  }
  return [...grupos.entries()]
    .map(([tag, entries]) => ({ tag, entries }))
    .sort((a, b) => a.tag.localeCompare(b.tag));
};

// Etiqueta de badge por tipo de restricción violada — vive acá (no en
// ValidadorEstructura.tsx) porque tanto el Panel de Restricciones como el
// reporte descargable (construirReporteTexto más abajo) la necesitan.
export const BADGE_RESTRICCION: Record<TipoRestriccionViolada, string> = {
  longitudMaxima: "Long. máxima",
  longitudMinima: "Long. mínima",
  soloNumerico: "Solo numérico",
  requerido: "Requerido",
  valoresPermitidos: "Valor no permitido"
};

// ── Resumen ejecutivo y reporte descargable ────────────────────────────────

export interface ResumenDiscrepancias {
  errores: number;
  faltantes: number;
  advertencias: number;
  camposNuevos: number;
}

/**
 * Cuenta las entradas ya reclasificadas (ver construirEntradas) por el
 * agrupado que usa el resumen ejecutivo del Panel de Discrepancias — separa
 * "errores" (longitud excedida que sí viola una restricción configurada) de
 * "advertencias" (longitud excedida sin esa violación), ambos dentro de la
 * misma categoría "longitud".
 */
export const resumirEntradas = (entradas: DiscrepanciaEntry[]): ResumenDiscrepancias => ({
  errores: entradas.filter((e) => e.categoria === "longitud" && e.severidad === "error").length,
  faltantes: entradas.filter((e) => e.categoria === "faltante").length,
  advertencias: entradas.filter((e) => e.categoria === "longitud" && e.severidad === "advertencia").length,
  camposNuevos: entradas.filter((e) => e.categoria === "nuevo").length
});

export interface DatosReportePlantilla {
  tipo: string;
  nombreArchivo?: string;
  alcance?: "global" | "proyecto";
}

/**
 * Arma el contenido de texto plano del reporte descargable — pura, sin
 * tocar el DOM (el Blob/descarga vive en ValidadorEstructura.tsx). Recibe
 * las mismas entradas ya reclasificadas que renderiza el Panel de
 * Discrepancias, así el reporte nunca puede desincronizarse de lo que el
 * usuario ve en pantalla.
 */
export const construirReporteTexto = (
  entradas: DiscrepanciaEntry[],
  resultadosRestricciones: ResultadoRestriccion[],
  plantillaInfo: DatosReportePlantilla,
  nombreDocumento: string
): string => {
  const resumen = resumirEntradas(entradas);
  const fecha = new Date().toLocaleString("es-CO");
  const alcanceTexto = plantillaInfo.alcance === "proyecto" ? "plantilla del proyecto" : "plantilla global";

  const lineas: string[] = [];
  lineas.push("REPORTE DE VALIDACIÓN - ONEHUB");
  lineas.push(`Fecha: ${fecha}`);
  lineas.push(`Plantilla: ${plantillaInfo.tipo} · ${plantillaInfo.nombreArchivo || "—"} · ${alcanceTexto}`);
  lineas.push(`Documento validado: ${nombreDocumento || "—"}`);
  lineas.push("");
  lineas.push("RESUMEN");
  lineas.push(`Errores: ${resumen.errores} | Faltantes: ${resumen.faltantes} | Advertencias: ${resumen.advertencias} | Campos nuevos: ${resumen.camposNuevos}`);
  lineas.push(`Restricciones violadas: ${resultadosRestricciones.length}`);
  lineas.push("");

  lineas.push("PANEL DE DISCREPANCIAS");
  if (entradas.length === 0) {
    lineas.push("Sin diferencias encontradas.");
  } else {
    entradas.forEach((entry) => {
      const pathVisual = formatearPathVisual(entry.path);
      lineas.push(`[${entry.badge}] ${pathVisual}`);
      if (entry.categoria === "longitud") {
        lineas.push(`  plantilla: "${entry.valorPlantilla ?? ""}" (${entry.largoPlantilla}) -> nuevo: "${entry.valorDocumento ?? ""}" (${entry.largoDocumento})`);
      } else if (entry.categoria === "faltante") {
        lineas.push(`  plantilla: "${entry.valorPlantilla ?? ""}"`);
      } else {
        lineas.push(`  nuevo: "${entry.valorDocumento ?? ""}"`);
      }
    });
  }
  lineas.push("");

  lineas.push("PANEL DE RESTRICCIONES");
  if (resultadosRestricciones.length === 0) {
    lineas.push("Sin restricciones violadas.");
  } else {
    resultadosRestricciones.forEach((r) => {
      lineas.push(`[${BADGE_RESTRICCION[r.restriccionViolada]}] ${r.tagConIndice}`);
      lineas.push(`  valor: "${r.valorActual}"`);
      lineas.push(`  ${r.detalle}`);
      if (r.descripcionAdmin) lineas.push(`  Nota: ${r.descripcionAdmin}`);
    });
  }

  return lineas.join("\n");
};
